import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiDefine } from '../utils/api';

// Wraps a result word so clicking it opens a small popover with the word's
// meaning and a reference to which dictionary the definition came from.
// The popover is fixed-positioned (computed from the trigger's rect) so it
// isn't clipped by scrolling tables or overflow containers.
export default function DefinableWord({ word, dict, className, style, children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = useCallback(async () => {
    if (open) { setOpen(false); return; }
    const r = triggerRef.current.getBoundingClientRect();
    const width = Math.min(300, window.innerWidth - 24);
    setPos({
      top: r.bottom + 6,
      left: Math.max(12, Math.min(r.left, window.innerWidth - width - 12)),
      width,
    });
    setOpen(true);
    if (data === null && !loading) {
      setLoading(true);
      try { const { data: d } = await apiDefine(word, dict); setData(d); }
      catch { setData({ found: false }); }
      finally { setLoading(false); }
    }
  }, [open, data, loading, word, dict]);

  return (
    <>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        className={className}
        style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3, ...style }}
        title="Click for definition"
      >
        {children}
      </span>
      {open && (
        <div
          ref={popRef}
          role="tooltip"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 1100,
            background: '#fff', color: '#374151',
            border: '1px solid var(--border)', borderRadius: 12,
            boxShadow: '0 8px 26px rgba(0,0,0,0.18)',
            padding: '0.8rem 0.9rem',
            fontFamily: 'Nunito, sans-serif', fontSize: '0.86rem', lineHeight: 1.5,
            textAlign: 'left', fontWeight: 500, maxHeight: '60vh', overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: 0.5 }}>
              {(word || '').toUpperCase()}
            </span>
            <button
              type="button" aria-label="Close" onClick={() => setOpen(false)}
              style={{ border: 'none', background: 'transparent', fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer', color: '#94a3b8', padding: 0 }}
            >×</button>
          </div>
          {loading ? (
            <div className="text-muted">Loading…</div>
          ) : data && data.found ? (
            <>
              <div style={{ marginBottom: 6 }}>{data.definition}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', borderTop: '1px solid var(--border)', paddingTop: 5 }}>
                📖 Source: {data.source}
              </div>
            </>
          ) : (
            <div className="text-muted">No definition found for “{(word || '').toLowerCase()}”.</div>
          )}
        </div>
      )}
    </>
  );
}
